import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, TrendingUp, DollarSign, Users, Briefcase, 
  BarChart3, PieChart, Calendar, ArrowUpRight, ArrowDownRight,
  Loader2, Target, Award, Zap, Eye
} from 'lucide-react';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_creator-brand-2/artifacts/feyyw8lo_145437-200.png";

// Simple Bar Chart Component
const BarChart = ({ data, color = '#3AAFB9' }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-2 h-40">
      {data.map((item, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full rounded-t-lg transition-all duration-500"
            style={{ 
              height: `${(item.value / maxValue) * 100}%`,
              background: `linear-gradient(180deg, ${color} 0%, ${color}99 100%)`,
              minHeight: item.value > 0 ? '8px' : '2px'
            }}
          />
          <span className="text-xs text-muted-foreground">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

// Donut Chart Component
const DonutChart = ({ data, size = 120 }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  let cumulativePercent = 0;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox="0 0 36 36" className="transform -rotate-90">
        {data.map((item, idx) => {
          const percent = (item.value / total) * 100;
          const strokeDasharray = `${percent} ${100 - percent}`;
          const strokeDashoffset = -cumulativePercent;
          cumulativePercent += percent;
          
          return (
            <circle
              key={idx}
              cx="18"
              cy="18"
              r="15.915"
              fill="none"
              stroke={item.color}
              strokeWidth="3"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-500"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold">{total}</span>
      </div>
    </div>
  );
};

// Stat Card with Trend
const StatCardWithTrend = ({ label, value, trend, trendValue, icon: Icon, color }) => (
  <Card className="overflow-hidden">
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{label}</p>
          <p className="text-3xl font-bold">{value}</p>
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${
              trend === 'up' ? 'text-green-500' : 'text-red-500'
            }`}>
              {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Analytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [analyticsData, setAnalyticsData] = useState(null);
  
  const isCreator = user?.role === 'creator';

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/analytics/dashboard?period=${period}`);
      setAnalyticsData(response.data);
    } catch (error) {
      // Use mock data if endpoint doesn't exist
      setAnalyticsData(generateMockData(isCreator));
    } finally {
      setLoading(false);
    }
  }, [period, isCreator]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Generate mock data for demo
  const generateMockData = (isCreator) => {
    if (isCreator) {
      return {
        totalEarnings: 4250,
        earningsTrend: '+12.5%',
        completedCampaigns: 8,
        campaignsTrend: '+2',
        avgRating: 4.8,
        profileViews: 1240,
        viewsTrend: '+18%',
        earningsChart: [
          { label: 'Jan', value: 320 },
          { label: 'Feb', value: 480 },
          { label: 'Mar', value: 620 },
          { label: 'Apr', value: 550 },
          { label: 'May', value: 780 },
          { label: 'Jun', value: 890 },
        ],
        campaignsByNiche: [
          { label: 'Tech', value: 4, color: '#3AAFB9' },
          { label: 'Lifestyle', value: 2, color: '#E8795A' },
          { label: 'Fashion', value: 1, color: '#F472B6' },
          { label: 'Other', value: 1, color: '#94A3B8' },
        ],
        recentPayments: [
          { campaign: 'Tech Product Launch', amount: 500, date: '2026-03-15', status: 'completed' },
          { campaign: 'Lifestyle Brand Collab', amount: 350, date: '2026-03-10', status: 'completed' },
          { campaign: 'Summer Collection', amount: 450, date: '2026-03-01', status: 'completed' },
        ],
        performanceMetrics: {
          responseRate: 95,
          completionRate: 100,
          avgResponseTime: '2h',
          repeatClients: 3,
        }
      };
    } else {
      return {
        totalSpent: 12500,
        spentTrend: '+8.2%',
        activeCampaigns: 3,
        completedCampaigns: 12,
        totalCreators: 28,
        creatorsTrend: '+5',
        avgROI: 340,
        roiTrend: '+15%',
        spendingChart: [
          { label: 'Jan', value: 1200 },
          { label: 'Feb', value: 1800 },
          { label: 'Mar', value: 2100 },
          { label: 'Apr', value: 1900 },
          { label: 'May', value: 2400 },
          { label: 'Jun', value: 3100 },
        ],
        campaignsByStatus: [
          { label: 'Completed', value: 12, color: '#22C55E' },
          { label: 'Active', value: 3, color: '#3AAFB9' },
          { label: 'Draft', value: 2, color: '#94A3B8' },
        ],
        topCreators: [
          { name: 'Sarah Tech', matchScore: 92, campaigns: 4, totalPaid: 2400 },
          { name: 'Mike Lifestyle', matchScore: 88, campaigns: 3, totalPaid: 1800 },
          { name: 'Emma Fashion', matchScore: 85, campaigns: 2, totalPaid: 1200 },
        ],
        roiByNiche: [
          { niche: 'Tech', roi: 420, spend: 4500 },
          { niche: 'Lifestyle', roi: 310, spend: 3200 },
          { niche: 'Fashion', roi: 280, spend: 2800 },
        ]
      };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen dashboard-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#3AAFB9]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen dashboard-bg relative overflow-hidden">
      {/* Creative Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="creator-orb creator-orb-teal" style={{ top: '-10%', right: '10%', opacity: 0.3 }} />
        <div className="creator-orb creator-orb-pink" style={{ bottom: '10%', left: '-5%', opacity: 0.25 }} />
        <div className="network-lines opacity-20" />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <header className="glass-nav sticky top-0 z-40 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="CmdOut" className="w-8 h-8" />
                <h1 className="text-xl font-bold">Analytics Dashboard</h1>
              </div>
            </div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="1y">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-8">
          {isCreator ? (
            /* Creator Analytics */
            <>
              {/* Stats Overview */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCardWithTrend 
                  label="Total Earnings" 
                  value={`$${analyticsData.totalEarnings.toLocaleString()}`}
                  trend="up"
                  trendValue={analyticsData.earningsTrend}
                  icon={DollarSign}
                  color="bg-gradient-to-br from-green-500 to-green-600"
                />
                <StatCardWithTrend 
                  label="Completed Campaigns" 
                  value={analyticsData.completedCampaigns}
                  trend="up"
                  trendValue={analyticsData.campaignsTrend}
                  icon={Briefcase}
                  color="bg-gradient-to-br from-[#3AAFB9] to-[#2F9AA3]"
                />
                <StatCardWithTrend 
                  label="Avg Rating" 
                  value={analyticsData.avgRating}
                  icon={Award}
                  color="bg-gradient-to-br from-[#E8795A] to-[#D76A4C]"
                />
                <StatCardWithTrend 
                  label="Profile Views" 
                  value={analyticsData.profileViews.toLocaleString()}
                  trend="up"
                  trendValue={analyticsData.viewsTrend}
                  icon={Eye}
                  color="bg-gradient-to-br from-[#F472B6] to-[#E879F9]"
                />
              </div>

              <div className="grid lg:grid-cols-3 gap-6 mb-8">
                {/* Earnings Chart */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#3AAFB9]" />
                      Earnings Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BarChart data={analyticsData.earningsChart} color="#3AAFB9" />
                  </CardContent>
                </Card>

                {/* Campaigns by Niche */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-[#E8795A]" />
                      Campaigns by Niche
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col items-center">
                    <DonutChart data={analyticsData.campaignsByNiche} />
                    <div className="flex flex-wrap gap-3 mt-4 justify-center">
                      {analyticsData.campaignsByNiche.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-sm">{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Performance Metrics */}
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-[#3AAFB9]" />
                    Performance Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-muted/50 rounded-xl">
                      <p className="text-3xl font-bold text-green-500">{analyticsData.performanceMetrics.responseRate}%</p>
                      <p className="text-sm text-muted-foreground">Response Rate</p>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-xl">
                      <p className="text-3xl font-bold text-[#3AAFB9]">{analyticsData.performanceMetrics.completionRate}%</p>
                      <p className="text-sm text-muted-foreground">Completion Rate</p>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-xl">
                      <p className="text-3xl font-bold text-[#E8795A]">{analyticsData.performanceMetrics.avgResponseTime}</p>
                      <p className="text-sm text-muted-foreground">Avg Response Time</p>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-xl">
                      <p className="text-3xl font-bold text-[#F472B6]">{analyticsData.performanceMetrics.repeatClients}</p>
                      <p className="text-sm text-muted-foreground">Repeat Clients</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Payments */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-500" />
                    Recent Payments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analyticsData.recentPayments.map((payment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                        <div>
                          <p className="font-medium">{payment.campaign}</p>
                          <p className="text-sm text-muted-foreground">{payment.date}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-500">+${payment.amount}</p>
                          <p className="text-xs text-green-500 capitalize">{payment.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            /* Brand Analytics */
            <>
              {/* Stats Overview */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCardWithTrend 
                  label="Total Spent" 
                  value={`$${analyticsData.totalSpent.toLocaleString()}`}
                  trend="up"
                  trendValue={analyticsData.spentTrend}
                  icon={DollarSign}
                  color="bg-gradient-to-br from-[#3AAFB9] to-[#2F9AA3]"
                />
                <StatCardWithTrend 
                  label="Active Campaigns" 
                  value={analyticsData.activeCampaigns}
                  icon={Briefcase}
                  color="bg-gradient-to-br from-[#E8795A] to-[#D76A4C]"
                />
                <StatCardWithTrend 
                  label="Total Creators" 
                  value={analyticsData.totalCreators}
                  trend="up"
                  trendValue={analyticsData.creatorsTrend}
                  icon={Users}
                  color="bg-gradient-to-br from-[#F472B6] to-[#E879F9]"
                />
                <StatCardWithTrend 
                  label="Avg ROI" 
                  value={`${analyticsData.avgROI}%`}
                  trend="up"
                  trendValue={analyticsData.roiTrend}
                  icon={TrendingUp}
                  color="bg-gradient-to-br from-green-500 to-green-600"
                />
              </div>

              <div className="grid lg:grid-cols-3 gap-6 mb-8">
                {/* Spending Chart */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#3AAFB9]" />
                      Spending Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BarChart data={analyticsData.spendingChart} color="#E8795A" />
                  </CardContent>
                </Card>

                {/* Campaigns by Status */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-[#E8795A]" />
                      Campaigns by Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col items-center">
                    <DonutChart data={analyticsData.campaignsByStatus} />
                    <div className="flex flex-wrap gap-3 mt-4 justify-center">
                      {analyticsData.campaignsByStatus.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-sm">{item.label} ({item.value})</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* ROI by Niche */}
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-[#3AAFB9]" />
                    ROI by Niche
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analyticsData.roiByNiche.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <div className="w-24 font-medium">{item.niche}</div>
                        <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#3AAFB9] to-[#E8795A] rounded-full"
                            style={{ width: `${Math.min(item.roi / 5, 100)}%` }}
                          />
                        </div>
                        <div className="w-20 text-right font-bold text-green-500">{item.roi}%</div>
                        <div className="w-24 text-right text-muted-foreground">${item.spend.toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top Creators */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#E8795A]" />
                    Top Performing Creators
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analyticsData.topCreators.map((creator, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-4 bg-muted/30 rounded-xl">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3AAFB9] to-[#E8795A] flex items-center justify-center text-white font-bold">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold">{creator.name}</p>
                          <p className="text-sm text-muted-foreground">{creator.campaigns} campaigns</p>
                        </div>
                        <div className="text-center px-4">
                          <p className="text-lg font-bold text-[#3AAFB9]">{creator.matchScore}</p>
                          <p className="text-xs text-muted-foreground">Match Score</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">${creator.totalPaid.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Total Paid</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Analytics;
